import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import { useParams, useHistory } from 'react-router-dom'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { getVolumes, getArticleDataById, getArticleById } from '../../../entity/volumes/Volumes'
import VolumesLayout from '../../../components/Layouts/VolumesLayout'
import { ImFilePdf } from 'react-icons/im'
import InnerArticle from '../components/InnerArticle/InnerArticle'
import CreateReferenceIcon from '../components/CreateReference/CreateReference'
import { getUserData } from '../../../entity/user/model/selector/getUserData/getUserData'

const Article = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const { t, i18n } = useTranslation()
  const { volumeId, issueId, articleId } = useParams()

  const user = useSelector(getUserData, shallowEqual)

  useEffect(() => {
    dispatch(getVolumes())
    dispatch(getArticleById(articleId))
  }, [dispatch, articleId])

  const articleData = useSelector(getArticleDataById, shallowEqual)

  const onGoBack = () => {
    history.goBack()
  }

  if (!articleData) {
    return (
      <>
        <Helmet>
          <meta charSet='utf-8' />
          <title>Loading Article - Moambe</title>
          <meta name='description' content='Loading article from Moambe journal.' />
        </Helmet>
        <VolumesLayout>{t('common.loading')}</VolumesLayout>
      </>
    )
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'ScholarlyArticle',
    headline: i18n.language === 'ka' ? articleData?.title_ka : articleData?.title,
    abstract: i18n.language === 'ka' ? articleData?.abstract_ka : articleData?.abstract,
    author: (i18n.language === 'ka' ? articleData?.authors_ka : articleData?.authors)?.map(
      author => ({
        '@type': 'Person',
        name: author,
      }),
    ),
    keywords: (i18n.language === 'ka' ? articleData?.keywords_ka : articleData?.keywords)?.join(
      ', ',
    ),
    datePublished: articleData?.created_at,
    publisher: {
      '@type': 'Organization',
      name: 'Moambe',
      url: 'http://moambe.org.ge',
    },
    url: `http://moambe.org.ge/volumes/${volumeId}/issue/${issueId}/article/${articleId}`,
    mainEntityOfPage: `http://moambe.org.ge/volumes/${volumeId}/issue/${issueId}/article/${articleId}`,
    inLanguage: i18n.language === 'ka' ? 'ka' : 'en',
    ...(articleData?.doi_url && { doi: articleData.doi_url }),
    ...(articleData?.pdf_url && { pdf: articleData.pdf_url }),
  }
  return (
    <>
      <Helmet>
        <meta charSet='utf-8' />
        <title>{`მოამბე - Moambe ${i18n.language === 'ka' ? articleData?.title_ka : articleData?.title} | მოამბე - Moambe`}</title>
        <meta
          name='description'
          content={
            articleData?.abstract?.substring(0, 160) || 'Scientific article from Moambe journal.'
          }
        />
        <link rel='canonical' href={`http://moambe.org.ge/volumes/article/${articleId}`} />

        {/* Open Graph / Facebook */}
        <meta property='og:type' content='article' />
        <meta property='og:title' content={articleData?.title} />
        <meta
          property='og:description'
          content={
            articleData?.abstract?.substring(0, 160) || 'Scientific article from Moambe journal.'
          }
        />
        <meta property='og:url' content={`http://moambe.org.ge/volumes/article/${articleId}`} />

        {/* Twitter */}
        <meta name='twitter:card' content='summary_large_image' />
        <meta name='twitter:title' content={articleData?.title} />
        <meta
          name='twitter:description'
          content={
            articleData?.abstract?.substring(0, 160) || 'Scientific article from Moambe journal.'
          }
        />
        <meta name='twitter:url' content={`http://moambe.org.ge/volumes/article/${articleId}`} />

        {/* Additional SEO meta tags */}
        <meta name='keywords' content={articleData?.keywords?.join(', ')} />
        <meta name='author' content={articleData?.authors?.join(', ')} />
        <meta name='date' content={articleData?.created_at} />
        <meta name='citation_doi' content={articleData?.doi_url} />
        <meta name='citation_pdf_url' content={articleData?.pdf_url} />

        {/* Structured Data */}
        <script type='application/ld+json'>{JSON.stringify(structuredData)}</script>
      </Helmet>
      <VolumesLayout>
        <article className='flex flex-col'>
          {articleData?.field && (
            <p className='text-[#ffffff] text-sm bg-[#897056] w-min rounded px-1 max-w-xs'>
              {i18n.language === 'ka' ? articleData?.field_ka : articleData?.field}
            </p>
          )}
          <header className='flex flex-row items-center gap-2'>
            <h1 className='text-[#042A54] text-2xl'>
              {i18n.language === 'ka' ? articleData?.title_ka : articleData?.title}
            </h1>
            {user?.role === 'admin' && <CreateReferenceIcon articleId={articleData?.id} />}
          </header>
          {(articleData?.pdf_url || articleData?.article?.doi_url) && (
            <div className='flex flex-row items-center gap-1.5'>
              {articleData?.pdf_url && (
                <a
                  href={articleData?.pdf_url || '#'}
                  className={`flex flex-row wrap gap-1 items-center  ${
                    articleData?.pdf_url ? 'text-[#002336]' : 'text-gray-400 hover:text-gray-400'
                  }`}
                >
                  <ImFilePdf color={articleData?.pdf_url ? 'red' : 'gray'} />
                  <span className=''>PDF</span>
                </a>
              )}
              {articleData?.pdf_url && articleData?.doi_url && <span>|</span>}
              {articleData?.doi_url && (
                <a
                  href={articleData?.doi_url ?? '#'}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='mb-0'
                >
                  {articleData?.doi_url}
                </a>
              )}
            </div>
          )}
          {articleData?.authors?.length > 0 && (
            <div className='flex flex-col gap-0.5 mt-4'>
              <h4 className='uppercase text-xl text-[#897056] mb-0'>{t('volume.Authors')}</h4>
              <div className='flex flex-row flex-wrap'>
                {(i18n.language === 'ka' ? articleData?.authors_ka : articleData?.authors)?.map(
                  (author, index) => (
                    <p key={author} className='text-[#042A54] text-xl text-wrap'>
                      {author}
                      {index !== articleData.authors.length - 1 && ','}&nbsp;
                    </p>
                  ),
                )}
              </div>
            </div>
          )}
          {articleData?.abstract && (
            <div className='flex flex-col gap-0.5 mt-4'>
              <h4 className='uppercase text-xl text-[#897056] mb-0'>{t('volume.Abstract')}</h4>
              <p className='text-[#042A54] text-xl text-wrap'>
                {i18n.language === 'ka' ? articleData?.abstract_ka : articleData?.abstract}
              </p>
            </div>
          )}

          {articleData?.keywords?.length > 0 && (
            <div className='flex flex-col gap-0.5 mt-4'>
              <h4 className='uppercase text-xl text-[#897056] mb-0'>{t('volume.Keywords')}</h4>
              <div className='flex flex-row flex-wrap'>
                {(i18n.language === 'ka' ? articleData?.keywords_ka : articleData?.keywords)?.map(
                  (keyword, index) => (
                    <p key={keyword} className='text-[#042A54] text-xl text-wrap'>
                      {keyword}
                      {index !== articleData?.authors?.length - 1 && ','}&nbsp; &nbsp;
                    </p>
                  ),
                )}
              </div>
            </div>
          )}

          {articleData?.references?.length > 0 && (
            <div className='flex flex-col gap-0.5 mt-4'>
              <h4 className='uppercase text-xl text-[#897056] mb-0'>{t('volume.References')}</h4>
              <div className='flex flex-col gap-2'>
                {articleData?.references?.map((reference, index) => (
                  <InnerArticle
                    key={index}
                    articleId={articleId}
                    reference={reference}
                    index={index}
                  />
                ))}
              </div>
            </div>
          )}
          <h3
            onClick={onGoBack}
            className='text-[#042A54] hover:no-underline underline mt-4 text-xl cursor-pointer'
          >
            {t('volume.Back to Issue')}
          </h3>
        </article>
      </VolumesLayout>
    </>
  )
}

export default Article
